import { useState } from "react";
import { motion } from "framer-motion";
import { useTheme } from "../hooks/useTheme";
import CanvasBreathingLight from "../components/CanvasBreathingLight";
import PencilGraphiteBackground from "../components/PencilGraphiteBackground";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProjectCard from "../components/ProjectCard";
import ProjectModal from "../components/ProjectModal";
import { projects, categories } from "../data/projects";

const Portfolio = () => {
  const { isDarkMode } = useTheme();
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedProject, setSelectedProject] = useState(null);

  const filtered =
    activeCategory === "all"
      ? projects
      : projects.filter((p) => p.category === activeCategory);

  return (
    <div
      className={`App relative min-h-screen transition-colors duration-700 ${
        isDarkMode ? "bg-graphite-darker" : "bg-graphite-darker"
      }`}
      role="main"
    >
      <CanvasBreathingLight />
      <PencilGraphiteBackground intensity="light" />

      <div className="relative z-10">
        <Header />

        <main className="pt-28 pb-20 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-20">
          {/* Page hero */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            className="text-center mb-14"
          >
            <h1
              className={`text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 ${
                isDarkMode ? "text-white" : "text-graphite-darkest"
              }`}
            >
              My Work
            </h1>
            <p
              className={`text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed ${
                isDarkMode ? "text-graphite-silver" : "text-graphite-medium"
              }`}
            >
              A selection of projects across web, Web3, games, and mobile — click
              any card for details.
            </p>

            {/* Decorative divider */}
            <div className="flex items-center justify-center gap-4 mt-8">
              <div
                className={`h-px flex-1 max-w-24 ${
                  isDarkMode ? "bg-graphite-medium/40" : "bg-gray-300"
                }`}
              />
              <div className="w-1.5 h-1.5 rounded-full bg-graphite-steel" />
              <div
                className={`h-px flex-1 max-w-24 ${
                  isDarkMode ? "bg-graphite-medium/40" : "bg-gray-300"
                }`}
              />
            </div>
          </motion.div>

          {/* Category filter */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.15, ease: "easeOut" }}
            className="flex flex-wrap justify-center gap-2 mb-12"
            role="tablist"
            aria-label="Filter projects by category"
          >
            {categories.map((cat) => (
              <button
                key={cat.id}
                role="tab"
                aria-selected={activeCategory === cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-5 py-2 rounded-full text-sm font-semibold border transition-all duration-200 ${
                  activeCategory === cat.id
                    ? isDarkMode
                      ? "bg-graphite-steel text-white border-graphite-steel"
                      : "bg-graphite-darkest text-white border-graphite-darkest"
                    : isDarkMode
                    ? "border-graphite-medium/30 text-graphite-silver hover:border-graphite-silver/60 hover:text-white"
                    : "border-gray-300 text-graphite-medium hover:border-gray-500 hover:text-graphite-darkest"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </motion.div>

          {/* Project grid */}
          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {filtered.map((project, index) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  index={index}
                  onClick={setSelectedProject}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-24">
              <p
                className={`text-lg ${
                  isDarkMode ? "text-graphite-light" : "text-graphite-slate"
                }`}
              >
                No projects in this category yet.
              </p>
            </div>
          )}
        </main>

        <Footer />
      </div>

      {/* Project detail modal */}
      <ProjectModal
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
      />
    </div>
  );
};

export default Portfolio;
